import { useEffect, useState } from "react";
import { Button } from "react-aria-components";
import cx from "classnames";

import { ChevronDown } from "components/icons/generated";
import Box from "ds/components/Box";
import Icon from "ds/components/Icon";
import Typography from "ds/components/Typography";
import MarkdownAssistantPanel from "components/markdown/MarkdownAssistantPanel";

import AiChatThinkingAvatarAnimated from "./AvatarAnimated";
import AiChatThinkingAvatarStatic from "./AvatarStatic";
import styles from "./styles.module.css";

const AUTO_CLOSE_DELAY = 1000;
const MS_IN_S = 1000;

type ThinkingProps = {
  text: string;
  isStreaming: boolean;
};

const getThinkingMessage = (isStreaming: boolean, duration: number | undefined) => {
  if (isStreaming) {
    return "Thinking...";
  }
  if (duration === undefined) {
    return "Thought for a moment";
  }
  return `Thought for ${duration}s`;
};

const Thinking = ({ text, isStreaming }: ThinkingProps) => {
  const [isOpen, setIsOpen] = useState(isStreaming);
  const [duration, setDuration] = useState<number | undefined>(undefined);
  const [hasAutoClosed, setHasAutoClosed] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [startTime, setStartTime] = useState<number | null>(null);
  const [isStreamedInitially] = useState(isStreaming);

  // Track duration when streaming starts and ends
  useEffect(() => {
    if (isStreaming) {
      if (startTime === null) {
        setStartTime(Date.now());
      }
    } else if (startTime !== null) {
      setDuration(Math.ceil((Date.now() - startTime) / MS_IN_S));
      setStartTime(null);
    }
  }, [isStreaming, startTime]);

  // Auto-close when streaming ends (once only, and only if user hasn't interacted)
  useEffect(() => {
    if (!isStreaming && isStreamedInitially && isOpen && !hasAutoClosed && !hasUserInteracted) {
      const timer = setTimeout(() => {
        setIsOpen(false);
        setHasAutoClosed(true);
      }, AUTO_CLOSE_DELAY);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [isStreaming, isOpen, hasAutoClosed, hasUserInteracted, isStreamedInitially]);

  return (
    <Box className={styles.thinking} direction="column" align="start" fullWidth>
      <Box gap="medium" align="center">
        {isStreaming ? <AiChatThinkingAvatarAnimated /> : <AiChatThinkingAvatarStatic />}
        <Button
          type="button"
          onPress={() => {
            setHasUserInteracted(true);
            setIsOpen((value) => !value);
          }}
          className={styles.header}
        >
          <Box align="center" gap="small">
            <Typography tag="span" variant="p-body3">
              {getThinkingMessage(isStreaming, duration)}
            </Typography>
            <Icon src={ChevronDown} className={isOpen ? styles.chevronOpen : styles.chevron} />
          </Box>
        </Button>
      </Box>

      <Box className={cx(styles.content, isOpen && styles.contentOpen)} fullWidth>
        <Box direction="column" className={styles.contentInner}>
          <Box direction="column" className={styles.scrollArea} padding="medium 0">
            <MarkdownAssistantPanel>{text}</MarkdownAssistantPanel>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Thinking;
